import React, { Component } from "react";
import { Button, Paper } from "@material-ui/core";
import { generatePath, RouteComponentProps, withRouter } from "react-router";
import {
  Design,
  DesignScreenGroupSyncStatus,
  DesignSourceType,
  DesignMediaType,
} from "services/entities/designs";
import "./index.scss";
import {
  AddVideoIcon,
  CheckedItemIcon,
  UncheckedItemIcon,
  EditIconOutlined,
} from "components/Icon";
import { SpaceScreenGroup } from "services/entities/spaces";
import RouteURLs from "RouteURLs";
import ProgressSpinner from "components/ProgressSpinner";
import { editScreenGroup } from "services/canva";
import { CancelledError, handleError } from "util/errors";
import DesignPreview from "components/designs/DesignPreview";
import DesignWalkThrough from "components/designs/DesignWalkThrough";

enum DesignListItemStatus {
  Edit,
  Preview,
  Walkthrough,
}

interface DesignListItemProps extends RouteComponentProps {
  design: Design;
  onDelete?: (design: Design) => void;
  onUpdated?: (design: Design) => void;
  onPreview?: (design: Design) => void;
  onBook?: (design: Design) => void;
}

interface DesignListItemState {
  status: DesignListItemStatus;
}

class DesignListItem extends Component<
  DesignListItemProps,
  DesignListItemState
> {
  static readonly MAX_REGION_HEIGHT = 120;
  static readonly MAX_TOTAL_REGION_WIDTH = 1000;

  state: DesignListItemState = {
    status: DesignListItemStatus.Edit,
  };

  onDeleteClick = () => {
    if (this.props.onDelete) {
      this.props.onDelete(this.props.design);
    }
  };

  onEditScreenGroupClick = (group: SpaceScreenGroup) => {
    if (this.props.design.sourceType === DesignSourceType.Canva) {
      editScreenGroup(this.props.design, group.code).then(
        () => {
          if (this.props.onUpdated) {
            this.props.onUpdated(this.props.design);
          }
        },
        (ex) => {
          if (!(ex instanceof CancelledError)) {
            handleError("Failed saving design", ex);
          }
        }
      );
    } else {
      const url = generatePath(RouteURLs.EDIT_DESIGN_SCREEN_GROUP, {
        designCode: this.props.design.code,
        screenGroupCode: group.code,
        mediaType: DesignMediaType.Image,
        screenWidth: group.resolution.width,
        screenHeight: group.resolution.height,
      });
      this.props.history.push(url);
    }
  };

  onAddScreenGroupVideoClick = (group: SpaceScreenGroup) => {
    const url = generatePath(RouteURLs.EDIT_DESIGN_SCREEN_GROUP, {
      designCode: this.props.design.code,
      screenGroupCode: group.code,
      mediaType: DesignMediaType.Video,
      screenWidth: group.resolution.width,
      screenHeight: group.resolution.height,
    });
    this.props.history.push(url);
  };

  onExitClick = () => {
    const url = RouteURLs.DASHBOARD;
    this.props.history.push(url);
  };

  onPreviewClick = () => {
    this.setState({ status: DesignListItemStatus.Preview });
  };

  onWalkThroughClick = () => {
    this.setState({ status: DesignListItemStatus.Walkthrough });
  };

  onCancelPreviewClick = () => {
    this.setState({ status: DesignListItemStatus.Edit });
  };

  onBookClick = () => {
    this.props.history.push(RouteURLs.BOOKINGS_LIST);
  };

  static formatSpaceName(name: string): string {
    if (name.toLowerCase().startsWith("the ")) {
      // starts with "The" - change first letter to lowercase
      return name[0].toLowerCase() + name.substring(1);
    } else {
      return name;
    }
  }

  renderScreenGroup(
    design: Design,
    group: SpaceScreenGroup,
    scale: number,
    num?: number
  ) {
    const designGroup = design.screenGroups.find(
      (sg) => sg.screenGroup.code === group.code
    );
    const x = group.bounds.x * scale;
    const y = group.bounds.y * scale;
    const width = group.bounds.width * scale;
    const height = group.bounds.height * scale;
    const syncStatus = designGroup?.syncStatus?.thumbnail;
    const syncing = syncStatus !== DesignScreenGroupSyncStatus.SYNCED;
    const initializing =
      syncStatus === DesignScreenGroupSyncStatus.INITIALIZING ||
      syncStatus === DesignScreenGroupSyncStatus.INIT_FAILED;
    let groupClassName = "screen-group";
    if (syncing) {
      groupClassName += " syncing";
    }
    if (initializing) {
      groupClassName += " disabled";
    }
    return (
      <div
        key={group.code}
        className={groupClassName}
        style={{ left: x, top: y, width: width, height: height }}
        onClick={
          !initializing ? () => this.onEditScreenGroupClick(group) : undefined
        }
      >
        {designGroup && designGroup.thumbnailImageURL && (
          <img
            src={designGroup.thumbnailImageURL.toString()}
            alt={group.name}
          />
        )}
        {(initializing || syncing) && <ProgressSpinner />}
        {num !== undefined ? <span className="number">{num}</span> : null}
      </div>
    );
  }

  renderScreenGroupEditButtons(
    design: Design,
    group: SpaceScreenGroup,
    num?: number
  ) {
    const designGroup = design.screenGroups.find(
      (sg) => sg.screenGroup.code === group.code
    );
    const elementsComplete =
      designGroup?.sourceData?.source_file_type === DesignMediaType.Image;
    const videoComplete =
      designGroup?.sourceData?.source_file_type === DesignMediaType.Video;
    return (
      <li key={group.code} className={num !== undefined ? "numbered" : ""}>
        {num !== undefined ? <span className="number">{num}:</span> : null}
        <div>
          <Button
            size="small"
            className="edit"
            onClick={() => this.onEditScreenGroupClick(group)}
            disabled={false}
            startIcon={<EditIconOutlined />}
          >
            Upload image
          </Button>
        </div>
        <Button
          size="small"
          className="edit-video"
          onClick={() => this.onAddScreenGroupVideoClick(group)}
          disabled={false}
          startIcon={<AddVideoIcon />}
        >
          Upload video
        </Button>
        {elementsComplete ? (
          <CheckedItemIcon className="status completed" />
        ) : (
          <UncheckedItemIcon className="status" />
        )}
        {videoComplete ? (
          <CheckedItemIcon className="video-status completed" />
        ) : (
          <UncheckedItemIcon className="video-status" />
        )}
      </li>
    );
  }

  renderRegions(design: Design) {
    let maxHeight = 0;
    let totalWidth = 0;
    for (const region of this.props.design.space.regions) {
      maxHeight = Math.max(maxHeight, region.size.height);
      totalWidth += region.size.width;
    }
    const scale = Math.min(
      DesignListItem.MAX_TOTAL_REGION_WIDTH / totalWidth,
      DesignListItem.MAX_REGION_HEIGHT / maxHeight
    );
    return (
      <ul className="regions">
        {design.space.regions.map((region) => {
          const width = region.size.width * scale;
          const height = region.size.height * scale;
          return (
            <li key={region.code}>
              <div className="diagram">
                <div
                  className="screen-group-container"
                  style={{ height: height, width: width }}
                >
                  {region.screenGroups.map((group, index) =>
                    this.renderScreenGroup(
                      design,
                      group,
                      scale,
                      region.screenGroups.length > 1 ? index + 1 : undefined
                    )
                  )}
                </div>
              </div>
              <div className="name">{region.name}</div>
              <ul className="screen-group-video-container">
                {region.screenGroups.map((group, index) =>
                  this.renderScreenGroupEditButtons(
                    design,
                    group,
                    region.screenGroups.length > 1 ? index + 1 : undefined
                  )
                )}
              </ul>
            </li>
          );
        })}
      </ul>
    );
  }

  render() {
    const design = this.props.design;
    const isInitialized = !design.screenGroups.find(
      (sg) =>
        sg.syncStatus.thumbnail === DesignScreenGroupSyncStatus.INITIALIZING ||
        sg.syncStatus.thumbnail === DesignScreenGroupSyncStatus.INIT_FAILED
    );
    const status = this.state.status;
    return (
      <Paper className="design-list-item">
        <h3>
          {design.name} at{" "}
          <span className="space">
            {DesignListItem.formatSpaceName(design.space.name)}
          </span>
        </h3>
        {status === DesignListItemStatus.Edit && this.renderRegions(design)}
        {status === DesignListItemStatus.Preview && (
          <DesignPreview design={design} />
        )}
        {status === DesignListItemStatus.Walkthrough && (
          <DesignWalkThrough design={design.code} />
        )}
        <div className="actions">
          {status === DesignListItemStatus.Preview && (
            <Button onClick={this.onWalkThroughClick} disabled={!isInitialized}>
              3D Walk Through
            </Button>
          )}
          {(status === DesignListItemStatus.Edit ||
            status === DesignListItemStatus.Walkthrough) && (
            <Button onClick={this.onPreviewClick} disabled={!isInitialized}>
              3D Preview
            </Button>
          )}
          {(status === DesignListItemStatus.Preview ||
            status === DesignListItemStatus.Walkthrough) && (
            <Button
              onClick={this.onCancelPreviewClick}
              disabled={!isInitialized}
            >
              Edit
            </Button>
          )}
          <Button>
            <a
              id="guide"
              target="_blank"
              rel="noopener noreferrer"
              href="https://6778582.fs1.hubspotusercontent-na1.net/hubfs/6778582/Design%20Tool/SCREEN%20RESOLUTIONS%20AND%20GUIDANCE.pdf"
            >
              Media Sizing Guide
            </a>
          </Button>
          <Button onClick={this.onDeleteClick}>Delete design</Button>
        </div>
      </Paper>
    );
  }
}

export default withRouter(DesignListItem);
